var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { toUrlQueryString } from '../../backend/utils';
import { formatISO } from 'date-fns';
import { AlertBaseClient } from './AlertBaseClient';
/**
 * Maps `AlertFilterData` (used by frontend) to `GetAlertsQueryParams` (used by backend).
 */
export function mapAlertFilterData(filterData, params) {
    return Object.assign({ searchText: filterData.searchText, title: filterData.title, subject: filterData.subject, sender: filterData.sender, sentByMe: filterData.sentByMe, createdByMe: filterData.createdByMe, dateCreatedFrom: filterData.dateCreatedFrom ? formatISO(filterData.dateCreatedFrom) : undefined, dateCreatedTo: filterData.dateCreatedTo ? formatISO(filterData.dateCreatedTo) : undefined, dateSentFrom: filterData.dateSentFrom ? formatISO(filterData.dateSentFrom) : undefined, dateSentTo: filterData.dateSentTo ? formatISO(filterData.dateSentTo) : undefined, notificationType: filterData.notificationType, pinned: filterData.pinned, sharedWith: filterData.sharedWith, statuses: filterData.statuses, secure: filterData.secure, searchData: filterData.searchData }, params);
}
export const alertPrefix = '/proxy/alert';
export const notifyPrefix = '/proxy/notify';
/**
 * Wraps Alert Service API methods. This is closely related to `alertServiceClient.ts` in backend code.
 */
export class AlertServiceClient extends AlertBaseClient {
    /**
     * A custom token provider must be provider. The one from `baseClientDefaults` will not work.
     * A default base URL will work.
     */
    constructor(tokenProvider, baseUrl) {
        super(tokenProvider, baseUrl);
        /**
         * GET /alert/api/v1/alerts
         */
        this.getAlerts = (request) => __awaiter(this, void 0, void 0, function* () {
            const params = Object.assign(Object.assign({}, mapAlertFilterData(request.filterData, {
                sortBy: request.sortColumnKey,
                ascending: !request.sortColumnDesc,
            })), { page: request.limit > 0 ? Math.floor(request.offset / request.limit) : 0, size: request.limit });
            const queryParams = Object.assign({ cacheBust: Date.now() }, params);
            // IMPORTANT!!! ALERT SERVICE EXPECTS THESE STATUSES ONLY IN UPPERCASE, BUT THE SPEC USES LOWERCASE, NEED TO FIX THIS
            if (params.statuses) {
                queryParams.statuses = [...params.statuses].map((status) => status.toUpperCase());
            }
            const response = yield this.axiosInstance({
                method: 'GET',
                url: `${alertPrefix}/api/v1/alerts${toUrlQueryString(Object.assign({ cacheBust: Date.now() }, queryParams))}`,
            });
            return response.data;
        });
        /**
         * GET /alert/api/v1/alerts/{alertId}
         */
        this.getAlert = (alertId, excludeFields) => __awaiter(this, void 0, void 0, function* () {
            const response = yield this.axiosInstance({
                method: 'GET',
                url: `${alertPrefix}/api/v1/alerts/${alertId}${toUrlQueryString({ cacheBust: Date.now(), excludeFields })}`,
            });
            return response.data;
        });
        /**
         * PUT /alert/api/v1/alerts/{alertId}/terminate
         */
        this.terminateAlert = (alertId) => __awaiter(this, void 0, void 0, function* () {
            const response = yield this.axiosInstance({
                method: 'PUT',
                url: `${alertPrefix}/api/v1/alerts/${alertId}/terminate?cacheBust=${Date.now()}`,
                data: {},
            });
            return response.data;
        });
        /**
         * PUT /alert/api/v1/alerts/{alertId}/pin
         */
        this.pinAlert = (alertId, alertPins) => __awaiter(this, void 0, void 0, function* () {
            const response = yield this.axiosInstance({
                method: 'PUT',
                url: `${alertPrefix}/api/v1/alerts/${alertId}/pin?cacheBust=${Date.now()}`,
                data: alertPins,
            });
            return response.data;
        });
        /**
         * PUT /alert/api/v1/alerts/{alertId}/order
         */
        this.orderAlert = (alertId, orderIndex) => __awaiter(this, void 0, void 0, function* () {
            const response = yield this.axiosInstance({
                method: 'PUT',
                url: `${alertPrefix}/api/v1/alerts/${alertId}/order?orderIndex=${orderIndex}&cacheBust=${Date.now()}`,
                data: {},
            });
            return response.data;
        });
        /**
         * POST /alert/api/v1/alerts/{alertId}/responder-searches
         */
        this.searchAlertResponders = (alertId, request) => __awaiter(this, void 0, void 0, function* () {
            const params = new URLSearchParams();
            params.set('page', (request.limit > 0 ? Math.floor(request.offset / request.limit) : 0).toString());
            params.set('size', request.limit.toString());
            params.set('cacheBust', Date.now().toString());
            const response = yield this.axiosInstance({
                method: 'POST',
                url: `${alertPrefix}/api/v1/alerts/${alertId}/responder-searches?${params.toString()}`,
                data: {
                    search: request.filterData.searchText,
                    status: request.filterData.status,
                    responses: request.filterData.responses,
                    type: request.filterData.type,
                    alertResponderFields: ['externalId', 'name', 'status', 'responseOptionId', 'responseTime', 'type', 'groupNames'],
                },
            });
            return response.data;
        });
        /**
         * POST /alert/api/v1/documents
         */
        this.saveDocuments = (documents) => __awaiter(this, void 0, void 0, function* () {
            const formData = new FormData();
            documents.forEach((fe) => formData.append('files', fe));
            const response = yield this.axiosInstance({
                method: 'POST',
                url: `${alertPrefix}/api/v1/documents/${toUrlQueryString({ cacheBust: Date.now() })}`,
                data: formData,
            });
            return response.data;
        });
        /**
         * GET /alert/api/v1/documents/{documentId}
         */
        this.getDocument = (id) => __awaiter(this, void 0, void 0, function* () {
            const response = yield this.axiosInstance({
                method: 'GET',
                url: `${alertPrefix}/api/v1/documents/${id}${toUrlQueryString({ cacheBust: Date.now() })}`,
                responseType: 'blob',
            });
            return response.data;
        });
        /**
         * PUT /alert/api/v1/documents
         */
        this.updateDocumentsScanResult = (ids) => __awaiter(this, void 0, void 0, function* () {
            const response = yield this.axiosInstance({
                method: 'PUT',
                url: `${alertPrefix}/api/v1/documents${toUrlQueryString({ cacheBust: Date.now() })}`,
                data: { ids: ids },
            });
            return response.data;
        });
        /**
         * GET /alert/api/v1/context/channels
         */
        this.getChannels = () => __awaiter(this, void 0, void 0, function* () {
            const response = yield this.axiosInstance({
                method: 'GET',
                url: `${alertPrefix}/api/v1/context/channels?cacheBust=${Date.now()}`,
            });
            return response.data;
        });
        /**
         * GET /alert/api/v1/context/owners
         */
        this.getOwners = () => __awaiter(this, void 0, void 0, function* () {
            const response = yield this.axiosInstance({
                method: 'GET',
                url: `${alertPrefix}/api/v1/context/owners?cacheBust=${Date.now()}`,
            });
            return response.data;
        });
        /**
         * GET /alert/api/v1/context/user
         */
        this.getUser = () => __awaiter(this, void 0, void 0, function* () {
            const response = yield this.axiosInstance({
                method: 'GET',
                url: `${alertPrefix}/api/v1/context/user?cacheBust=${Date.now()}`,
            });
            return response.data;
        });
    }
}
